import { useId, useMemo, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { fhir, scheduleH1Entry, type HeldRecord } from '@curentia/shared';
import { ErrorText, Field, LockButton, Page, Submit } from '../components';
import { formatDate, formatDateTime, formatMonth } from '../lib/health';
import { MEDICINES } from '../lib/medicines';
import type { PracticeRecords } from '../lib/records';
import { registerCsv, registerRows } from '../lib/register';
import { useLoad } from '../lib/useLoad';
import { useAction, useDevice, useSnapshot } from '../state';

export const DISPENSING_LABELS: Readonly<Record<fhir.DispensingStatus, string>> = {
  waiting: 'Waiting',
  partly: 'Partly dispensed',
  dispensed: 'Dispensed',
  not_dispensed: 'Not dispensed',
};

export function DispensingBadge({ status }: { status: fhir.DispensingStatus }) {
  return (
    <span className={`badge dispensing-${status}`} data-testid="dispensing-status">
      {DISPENSING_LABELS[status]}
    </span>
  );
}

/** A dispensing record, read, with the practice that recorded it. */
export interface DispenseEntry {
  id: string;
  by: string;
  view: fhir.DispenseView;
}

/** Dispensing records as entries, oldest first. Records this version cannot read are left out. */
export function dispenseEntries(records: readonly { id: string; resource: Record<string, unknown>; by: string }[]): DispenseEntry[] {
  const entries: DispenseEntry[] = [];
  for (const record of records) {
    try {
      entries.push({ id: record.id, by: record.by, view: fhir.readDispenseRecord(record.resource as unknown as fhir.Bundle) });
    } catch {
      // A record this version cannot read is not shown.
    }
  }
  return entries.sort((a, b) => a.view.date.localeCompare(b.view.date));
}

/** One line for an item: what was handed over, how much, from which batch, and any substitution. */
function itemLine(item: fhir.DispensedView, prescription: fhir.PrescriptionView): string {
  const prescribed = prescription.medicines.find((m) => m.requestId === item.requestId)?.name;
  if (item.status !== 'completed') return `Not dispensed: ${prescribed ?? item.medicine}${item.note ? `. ${item.note}` : ''}`;
  const name = item.substituted && prescribed ? `${item.medicine}, given for ${prescribed}` : item.medicine;
  const details = [
    item.quantity ? `${item.quantity.value} ${item.quantity.unit}` : null,
    item.batch ? `batch ${item.batch.number}${item.batch.expiry ? `, expires ${formatMonth(item.batch.expiry)}` : ''}` : null,
  ].filter(Boolean);
  return `${name}${details.length > 0 ? `: ${details.join(', ')}` : ''}${item.substituted && item.substitutionReason ? `. Substituted: ${item.substitutionReason}` : ''}`;
}

/** What a prescription's dispensing records say, oldest first, with each record's practice and pharmacist. */
export function DispensingHistory({ prescription, entries }: { prescription: fhir.PrescriptionView; entries: DispenseEntry[] }) {
  if (entries.length === 0) return null;
  return (
    <ol className="timeline" aria-label="Dispensing">
      {entries.map((entry) => (
        <li key={entry.id}>
          <time dateTime={entry.view.date}>{formatDate(entry.view.date)}</time>
          <div className="timeline-body">
            <strong>{entry.by}</strong>
            <ul>
              {entry.view.items.map((item, i) => (
                <li key={i}>{itemLine(item, prescription)}</li>
              ))}
            </ul>
            <span className="muted">
              {entry.view.pharmacist.name}
              {entry.view.pharmacist.qualifications ? `, ${entry.view.pharmacist.qualifications}` : ''} · {formatDateTime(entry.view.date)}
            </span>
          </div>
        </li>
      ))}
    </ol>
  );
}

// ---- Recording what is handed over ---------------------------------------------------------------

const UNITS = ['tablets', 'capsules', 'bottles', 'tubes', 'strips', 'sachets', 'inhalers', 'vials', 'ampoules', 'packs', 'units'] as const;

/** The unit a medicine is counted in, from the form its name gives. */
export function unitFor(medicine: string): string {
  const name = medicine.toLowerCase();
  if (/tablet/.test(name)) return 'tablets';
  if (/capsule/.test(name)) return 'capsules';
  if (/syrup|suspension|solution|drops|lotion|mouthwash|gargle|spray|infusion/.test(name)) return 'bottles';
  if (/cream|gel|ointment|paste/.test(name)) return 'tubes';
  if (/sachet|powder/.test(name)) return 'sachets';
  if (/inhaler|respules/.test(name)) return 'inhalers';
  if (/injection/.test(name)) return 'vials';
  return 'units';
}

/**
 * How many to hand over for a course, when the prescription says: the dose
 * count, times a day and days, such as 1 tablet twice a day for 5 days = 10.
 */
export function suggestedQuantity(medicine: fhir.MedicineView): number | null {
  const count = Number(/^\s*(\d+(?:\.\d+)?)\s*(tablet|capsule)/i.exec(medicine.dosage)?.[1]);
  if (!medicine.timesPerDay || !medicine.durationDays || !Number.isFinite(count) || count <= 0) return null;
  return Math.ceil(count * medicine.timesPerDay * medicine.durationDays);
}

type Action = 'give' | 'substitute' | 'skip';

interface ItemRow {
  requestId: string;
  prescribed: string;
  action: Action;
  quantity: string;
  unit: string;
  batch: string;
  expiry: string;
  substitute: string;
  reason: string;
}

const BATCH = /^[A-Za-z0-9][A-Za-z0-9/ .-]{0,39}$/;

/** YYYY-MM of a time in this device's own time zone. */
const monthOf = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
const thisMonth = () => monthOf(new Date());

/** The dispensing items from the form, or a message. */
function itemsFrom(rows: ItemRow[]): fhir.DispensedItem[] | string {
  const items: fhir.DispensedItem[] = [];
  for (const row of rows) {
    if (row.action === 'skip') {
      items.push({ medicine: row.prescribed, requestId: row.requestId, status: 'declined' });
      continue;
    }
    const medicine = row.action === 'substitute' ? row.substitute.trim() : row.prescribed;
    if (row.action === 'substitute') {
      if (!medicine) return `Enter the medicine given in place of ${row.prescribed}.`;
      if (medicine.length > 200) return 'Use 200 characters or fewer for the substitute.';
      if (!row.reason.trim()) return `Say why ${medicine} was given in place of ${row.prescribed}.`;
    }
    const quantity = Number(row.quantity);
    if (!Number.isInteger(quantity) || quantity < 1 || quantity > 10_000) return `Enter how many ${row.unit} of ${medicine} you hand over.`;
    const batch = row.batch.trim();
    if (batch && !BATCH.test(batch)) return `Check the batch number of ${medicine}: letters, digits, spaces, dots, dashes and slashes only.`;
    if (row.expiry && !batch) return `Enter the batch number of ${medicine} with its expiry.`;
    if (row.expiry && row.expiry < thisMonth()) return `That batch of ${medicine} has expired. Hand over another.`;
    items.push({
      medicine,
      requestId: row.requestId,
      status: 'completed',
      quantity: { value: quantity, unit: row.unit },
      ...(batch ? { batch: { number: batch, ...(row.expiry ? { expiry: row.expiry } : {}) } } : {}),
      ...(row.action === 'substitute' ? { substitution: { reason: row.reason.trim().slice(0, 200) } } : {}),
    });
  }
  return items;
}

function ItemFields({ row, index, listId, onChange }: { row: ItemRow; index: number; listId: string; onChange: (row: ItemRow) => void }) {
  const actionId = useId();
  const unitId = useId();
  const set = (key: keyof ItemRow) => (event: { target: { value: string } }) => onChange({ ...row, [key]: event.target.value });
  const given = row.action === 'substitute' ? row.substitute : row.prescribed;
  const h1 = scheduleH1Entry(given);
  return (
    <fieldset className="medicine">
      <legend>
        {index + 1}. {row.prescribed}
      </legend>
      {h1 ? (
        <p className="note" data-testid="schedule-h1">
          Schedule H1 ({h1}). It goes in the Schedule H1 register.
        </p>
      ) : null}
      <div className="field">
        <label htmlFor={actionId}>What you do</label>
        <select id={actionId} value={row.action} onChange={set('action')}>
          <option value="give">Hand it over</option>
          <option value="substitute">Hand over a substitute</option>
          <option value="skip">Not now</option>
        </select>
      </div>
      {row.action === 'substitute' ? (
        <>
          <Field label="Substitute given" list={listId} autoComplete="off" value={row.substitute} onChange={set('substitute')} />
          <Field label="Why" hint="Such as the same medicine from another maker, or out of stock" value={row.reason} onChange={set('reason')} />
        </>
      ) : null}
      {row.action !== 'skip' ? (
        <>
          <div className="pair">
            <Field label="Quantity" inputMode="numeric" value={row.quantity} onChange={set('quantity')} />
            <div className="field">
              <label htmlFor={unitId}>Unit</label>
              <select id={unitId} value={row.unit} onChange={set('unit')}>
                {UNITS.map((unit) => (
                  <option key={unit} value={unit}>
                    {unit}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <Field label="Batch number" autoComplete="off" autoCapitalize="characters" value={row.batch} onChange={set('batch')} />
          <Field label="Expiry" type="month" min={thisMonth()} value={row.expiry} onChange={set('expiry')} />
        </>
      ) : null}
    </fieldset>
  );
}

/**
 * The pharmacist's record of what they hand over against a prescription, for
 * the medicines not handed over yet. Saving keeps it at the practice's record
 * holder, which notifies the patient and the prescribing doctor.
 */
export function DispenseForm({
  records,
  prescription,
  view,
  progress,
  onSaved,
}: {
  records: PracticeRecords;
  prescription: HeldRecord;
  view: fhir.PrescriptionView;
  progress: fhir.MedicineProgress[];
  onSaved: () => void;
}) {
  const { account } = useSnapshot();
  const listId = useId();
  const open = progress.filter((p) => p.handedOver.length === 0);
  const [rows, setRows] = useState<ItemRow[]>(() =>
    open.map(({ medicine }) => ({
      requestId: medicine.requestId,
      prescribed: medicine.name,
      action: 'give',
      quantity: String(suggestedQuantity(medicine) ?? ''),
      unit: unitFor(medicine.name),
      batch: '',
      expiry: '',
      substitute: '',
      reason: '',
    })),
  );
  const { busy, error, setError, run } = useAction();
  if (open.length === 0) return null;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!account?.registration) return setError('Your registration number is missing from your account.');
    const items = itemsFrom(rows);
    if (typeof items === 'string') return setError(items);
    void run(async () => {
      const patient = (await records.fetchRecords(prescription.patientId)).find((r) => r.kind === 'patient');
      if (!patient) throw new Error('The patient’s record could not be opened.');
      const details = fhir.patientDetails(patient.resource);
      const practice = records.practice;
      const bundle = fhir.buildDispenseRecord({
        date: new Date().toISOString(),
        patient: {
          name: details.name,
          ...(details.accountId ? { accountId: details.accountId } : {}),
          medicalRecord: details.medicalRecord ?? { value: patient.id, assigner: practice.name },
        },
        pharmacist: { name: account.name, registration: account.registration ?? { council: '', number: '' }, ...(account.qualifications ? { qualifications: account.qualifications } : {}) },
        pharmacy: {
          name: practice.name,
          practiceId: practice.id,
          city: practice.city,
          ...(practice.address ? { address: practice.address } : {}),
          ...(practice.phone ? { phone: practice.phone } : {}),
        },
        prescriptionId: view.id,
        items,
      });
      await records.save({
        id: globalThis.crypto.randomUUID(),
        patientId: prescription.patientId,
        kind: 'dispense',
        prescriptionId: prescription.id,
        resource: bundle as unknown as Record<string, unknown>,
        baseVersion: 0,
      });
      onSaved();
    });
  };

  return (
    <form onSubmit={submit} noValidate aria-label="Record dispensing" className="prescription-form">
      <h3>Record what you hand over</h3>
      <datalist id={listId}>
        {MEDICINES.map((name) => (
          <option key={name} value={name} />
        ))}
      </datalist>
      {rows.map((row, index) => (
        <ItemFields key={row.requestId} row={row} index={index} listId={listId} onChange={(next) => setRows(rows.map((r) => (r.requestId === row.requestId ? next : r)))} />
      ))}
      <p className="muted">Saving tells the patient and the prescribing doctor what you handed over.</p>
      <ErrorText error={error} />
      <Submit busy={busy} busyText="Saving…">
        Record and notify
      </Submit>
    </form>
  );
}

// ---- The dispensing register ----------------------------------------------------------------------

/**
 * The practice's dispensing register: every medicine handed over, with the
 * prescriber's name, registration and address, the patient, the quantity
 * and batch. The Schedule H1 view is the separate register the Drugs Rules
 * ask for. Both download as CSV, made on this device.
 */
export function RegisterScreen() {
  const { id = '' } = useParams();
  const device = useDevice();
  const { practicesVersion, recordsVersion } = useSnapshot();
  const opened = useLoad(() => device.records(id), [device, id, practicesVersion]);
  const records = opened.data;
  const loaded = useLoad(async () => {
    if (!records) return null;
    const [dispenses, prescriptions] = await Promise.all([records.register(), records.documents('Prescription')]);
    return registerRows(dispenses, new Map(prescriptions.map((p) => [p.id, p])));
  }, [records, recordsVersion]);
  const [onlyH1, setOnlyH1] = useState(false);
  const [month, setMonth] = useState('');
  const rows = useMemo(
    () => (loaded.data ?? []).filter((row) => (!onlyH1 || row.scheduleH1 !== '') && (!month || monthOf(new Date(row.date)) === month)),
    [loaded.data, onlyH1, month],
  );
  const download = () => {
    const blob = new Blob([registerCsv(rows)], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${onlyH1 ? 'schedule-h1-register' : 'dispensing-register'}${month ? `-${month}` : ''}.csv`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  const error = opened.error ?? loaded.error;
  return (
    <Page actions={<LockButton />}>
      <h1>Dispensing register</h1>
      {records ? <p className="muted">{records.practice.name}</p> : null}
      <ErrorText error={error} />
      <section className="card" aria-labelledby="register-filter">
        <h2 id="register-filter">Show</h2>
        <label className="check">
          <input type="checkbox" checked={onlyH1} onChange={(e) => setOnlyH1(e.target.checked)} />
          <span>Schedule H1 only</span>
        </label>
        <Field label="Month (optional)" type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
        <button type="button" className="button secondary" disabled={rows.length === 0} onClick={download}>
          Download as CSV
        </button>
        <p className="muted spaced">The file is made on this device and is not encrypted. Keep it as safely as the paper register.</p>
      </section>
      <section className="card" aria-labelledby="register-heading">
        <h2 id="register-heading">{onlyH1 ? 'Schedule H1 register' : 'All supplies'}</h2>
        {!loaded.data && !error ? <p className="muted">Loading…</p> : null}
        {loaded.data && rows.length === 0 ? <p className="muted">Nothing recorded{onlyH1 ? ' under Schedule H1' : ''}{month ? ' in that month' : ''}.</p> : null}
        {rows.length > 0 ? (
          <ol className="list register" aria-label="Register">
            {rows.map((row, i) => (
              <li key={`${row.prescriptionId}:${row.date}:${i}`} className="member">
                <strong>
                  {i + 1}. {row.medicine} · {row.quantity}
                </strong>
                <span>
                  {formatDate(row.date)} · {row.patient}
                </span>
                <span className="muted">
                  Prescribed by {row.prescriber}
                  {row.prescriberRegistration ? ` (${row.prescriberRegistration})` : ''}
                  {row.prescriberAddress ? `, ${row.prescriberAddress}` : ''}
                </span>
                {row.batch ? (
                  <span className="muted">
                    Batch {row.batch}
                    {row.expiry ? `, expires ${formatMonth(row.expiry)}` : ''}
                  </span>
                ) : null}
                {row.substitution ? <span className="muted">Given for {row.prescribed}: {row.substitution}</span> : null}
                {row.scheduleH1 ? <span className="badge schedule-h1">Schedule H1</span> : null}
              </li>
            ))}
          </ol>
        ) : null}
      </section>
      <div className="links">
        <Link to={`/practices/${id}`}>Back</Link>
      </div>
    </Page>
  );
}
